import { Button, cn } from '@newfront-insurance/core-ui/v2';
import type { FileAttachment } from '@newfront-insurance/nova-core';
import { ArrowUpIcon, Loader2 } from 'lucide-react';

import { useChatInputContext } from './chat-input-context';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface SendButtonProps {
  className?: string;
}

/**
 * Send button component for chat input.
 * Uses context to access input state and submit handler.
 * Shows loading state when chat is submitting/streaming.
 */
export function SendButton({ className }: SendButtonProps) {
  const { input, handleSubmit, status } = useChatInputContext();
  const { additionalContext } = useChatOverlayContext();

  const attachments = (additionalContext?.attachments as FileAttachment[]) ?? [];
  const hasUploadingFiles = attachments.some((attachment) => attachment.isUploading);
  const isDisabled = (input.length === 0 && attachments.length === 0) || hasUploadingFiles;

  if (['submitted', 'streaming'].includes(status)) {
    return (
      <Button
        data-testid="loading-button"
        className={cn('h-fit rounded-full border p-2', 'text-[#1C2A5A] hover:opacity-90', className)}
        style={{
          background: 'linear-gradient(97deg, #A4F5FF 0%, #E7FC53 122.24%)',
          borderColor: 'transparent',
        }}
        disabled
      >
        <Loader2 size={14} className="animate-spin" />
      </Button>
    );
  }

  return (
    <Button
      data-testid="send-button"
      className={cn('h-fit rounded-full border p-2', 'hover:opacity-90', className, {
        'cursor-not-allowed opacity-50': isDisabled,
      })}
      style={{
        background: isDisabled ? '#e5e7eb' : 'linear-gradient(97deg, #A4F5FF 0%, #E7FC53 122.24%)',
        borderColor: 'transparent',
      }}
      onClick={(event) => {
        event.preventDefault();
        if (!isDisabled) {
          handleSubmit(undefined);
        }
      }}
      disabled={isDisabled}
    >
      <ArrowUpIcon size={14} color="#1C2A5A" />
    </Button>
  );
}
